import type { ReactNode } from "react";
import { cx } from "../cx";
import { c } from "./colors";

export interface IllustrationProps {
  /** Width and height in CSS pixels (default 160). */
  readonly size?: number | undefined;
  readonly className?: string | undefined;
}

export type IllustrationTone = "primary" | "accent" | "success" | "danger";

const TONES = {
  primary: [c.p50, c.p100, c.p200],
  accent: [c.a50, c.a100, c.a200],
  success: [c.s50, c.s100, c.s200],
  danger: [c.d50, c.d100, c.d200],
} as const;

// Mirror-symmetric blob, so every scene reads the same in LTR and RTL.
const BLOB =
  "M80 12C110 12 140 32 144 68C148 104 124 144 80 148C36 144 12 104 16 68C20 32 50 12 80 12Z";

/** Shared decorative frame: purely visual, hidden from assistive tech, no text. */
export function Frame({
  size = 160,
  className,
  tone = "primary",
  children,
}: IllustrationProps & { readonly tone?: IllustrationTone; readonly children: ReactNode }) {
  const [outer, inner, dot] = TONES[tone];
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      width={size}
      height={size}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cx("shrink-0", className)}
    >
      <path d={BLOB} fill={outer} />
      <path d={BLOB} fill={inner} transform="translate(13 13) scale(.84)" />
      <circle cx="24" cy="40" r="3.5" fill={dot} />
      <circle cx="136" cy="40" r="3.5" fill={dot} />
      <circle cx="30" cy="134" r="2.5" fill={dot} />
      <circle cx="130" cy="134" r="2.5" fill={dot} />
      {children}
    </svg>
  );
}
